import classNames from 'classnames';
import type { FC, HTMLAttributes, LabelHTMLAttributes, ReactNode } from 'react';
import { TooltipIcon, type TooltipIconSize } from '../Tooltip/TooltipIcon';

type LabelSize = 'small' | 'default' | 'large' | 'x-large';

export type LabelProps = (
  | ({
      renderAs?: 'label';
      /**
       * See useHtmlId hook. That's probably what you want to use to populate htmlFor.
       *
       * The property is mandatory to prevent accidental omission, which is a frequent
       * issue of labels.
       *
       * Omission is allowed when the labeled control is inside the label.
       * Any other reason must be documented ad hoc.
       *
       * When you need to render an element visually looking as Label
       * but not being an actual label, `renderAs='div'` should be used.
       */
      htmlFor: string | undefined;
    } & LabelHTMLAttributes<HTMLLabelElement>)
  | ({
      renderAs: 'div';
    } & HTMLAttributes<HTMLDivElement>)
) & {
  children: ReactNode;
  size?: LabelSize;
  tooltipContent?: ReactNode;
  tooltipHeadline?: string;
};

const sizeClassName: { [Key in LabelSize]: string } = {
  small: 'text-xs',
  default: 'text-sm',
  large: 'text-base',
  'x-large': 'text-lg',
};

export const fromLabelToTooltipIconSize: {
  [Key in LabelSize]: TooltipIconSize;
} = {
  small: 'xs',
  default: 'sm',
  large: 'base',
  'x-large': 'lg',
};

/**
 * Component provides a general purpose label for inputs, text fields, etc. It provides an optional tooltip icon.
 * For checkbox/radio use FormControlLabel instead.
 */
export const Label: FC<LabelProps> = ({
  children,
  className,
  size = 'default',
  tooltipContent,
  tooltipHeadline,
  renderAs = 'label',
  ...restProps
}) => {
  const DynamicComponent: any = renderAs;

  return (
    <DynamicComponent
      className={classNames(
        'inline-flex gap-x-1 text-default font-semibold',
        'mb-0', // this is a workaround to locally reset the default bottom margin of the label tag from bootstrap.min.css
        sizeClassName[size],
        className,
      )}
      {...restProps}
    >
      {children}
      {tooltipContent && (
        <TooltipIcon
          size={fromLabelToTooltipIconSize[size]}
          content={tooltipContent}
          headline={tooltipHeadline}
        />
      )}
    </DynamicComponent>
  );
};
